import { message, Spin } from 'antd'
import { useCallback, useMemo, useRef, useState } from 'react'
import { getEpicsList } from '../../../../sprints.service'
import axios from 'axios'
import {
    apiGetTagList,
    getEpicsListForTC,
    getSprintListForTC,
} from '../../../../redux/testCases/testCase.service'

function useDebouncedCallback(cb, delay = 400) {
    const t = useRef()
    return useCallback(
        (...args) => {
            if (t.current) window.clearTimeout(t.current)
            t.current = window.setTimeout(() => cb(...args), delay)
        },
        [cb, delay]
    )
}

// Generic hook to power antd <Select> with server search + infinite scroll
export function useAsyncSelect(fetcher, pageSize = 20) {
    const [open, setOpen] = useState(false)
    const [options, setOptions] = useState([])
    const [loading, setLoading] = useState(false)
    const [search, setSearch] = useState('')
    const [page, setPage] = useState(1)
    const [hasMore, setHasMore] = useState(true)

    const load = useCallback(
        async (nextPage, nextSearch, replace) => {
            setLoading(true)
            try {
                const { items, hasMore } = await fetcher({
                    search: nextSearch,
                    page: nextPage,
                    pageSize,
                })
                setOptions((prev) => (replace ? items : [...prev, ...items]))
                setHasMore(hasMore)
                setPage(nextPage)
            } catch (e) {
                message.error('Failed to load options.')
            } finally {
                setLoading(false)
            }
        },
        [fetcher, pageSize]
    )

    const onDropdownVisibleChange = useCallback(
        (v) => {
            setOpen(v)
            if (v && options.length === 0 && !loading) {
                load(1, search, true)
            }
        },
        [load, options.length, loading, search]
    )

    const debouncedSearch = useDebouncedCallback((q) => {
        const cleaned = q.trim()
        setSearch(cleaned)
        setHasMore(true)
        load(1, cleaned, true)
    }, 400)

    const onPopupScroll = useCallback(
        (e) => {
            const el = e.currentTarget
            const nearBottom =
                el.scrollTop + el.clientHeight >= el.scrollHeight - 10
            if (nearBottom && hasMore && !loading) {
                load(page + 1, search, false)
            }
        },
        [hasMore, loading, load, page, search]
    )

    const notFoundContent = useMemo(
        () => (loading ? <Spin size="small" /> : null),
        [loading]
    )

    return {
        open,
        options,
        loading,
        notFoundContent,
        onDropdownVisibleChange,
        onPopupScroll,
        onSearch: debouncedSearch,
        reset: () => {
            setOptions([])
            setHasMore(true)
            setPage(1)
        },
    }
}

/* ---------------- API mappers ---------------- */
// Returns { items: [{value,label}], hasMore: boolean }

const currentProjectId =
    JSON.parse(localStorage.getItem('currentProject'))?.id || ''

export async function fetchEpics({ search, page, pageSize }) {
    const payload = {
        skip: (page - 1) * pageSize,
        limit: pageSize,
        projectId: currentProjectId,
        ...(search ? { title: search } : {}),
        filter: {
            ...(search ? { title: search } : {}),
        },
    }
    const params = new URLSearchParams(payload).toString()
    const res = await getEpicsListForTC(params)
    const data = Array.isArray(res?.data) ? res.data : res?.data?.data ?? []
    return {
        items: data.map((e) => ({ value: e._id, label: e.title })),
        hasMore: data.length === pageSize, // or use res.data.is_next if your API provides it
    }
}

export async function fetchSprints({ search, page, pageSize }) {
    const payload = {
        skip: (page - 1) * pageSize,
        limit: pageSize,
        ...(search ? { search } : {}),
        filter: {
            populate: {
                name: 'projectId',
                val: 'name',
            },
            projectId: currentProjectId,
        },
    }
    const res = await getSprintListForTC(payload)
    const list = res?.data?.data ?? res?.data ?? []
    const isNext = res?.data?.is_next
    return {
        items: list.map((s) => ({ value: s._id, label: s.title })),
        hasMore:
            typeof isNext === 'boolean' ? isNext : list.length === pageSize,
    }
}

export async function fetchTags({ search, page, pageSize }) {
    const params = {
        skip: (page - 1) * pageSize,
        limit: pageSize,
        ...(search ? { search } : {}),
        // sort: 'createdAt',
        // sortType: 'asc',
    }
    const res = await apiGetTagList(params)
    const list = res?.data?.data ?? res?.data ?? []
    const isNext = res?.data?.isNext
    return {
        items: list.map((t) => ({ value: t._id, label: t.name })),
        hasMore:
            typeof isNext === 'boolean' ? isNext : list.length === pageSize,
    }
}

export const getCurrentProjectId = () => {
    return JSON.parse(localStorage.getItem('currentProject'))?.id || ''
}

// Utility to check if a date is valid
function isValidDate(d) {
    return d instanceof Date && !isNaN(d)
}

// Format date/time with an optional template
export function fDateTime(date, options) {
    const d = new Date(date)
    if (!isValidDate(d)) {
        return 'Invalid date'
    }

    // Default format: YYYY-MM-DD HH:mm:ss
    const defaultOptions = {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    }

    const formatter = new Intl.DateTimeFormat(
        'en-GB',
        options ?? defaultOptions
    )
    return formatter.format(d)
}

// Format only date (YYYY-MM-DD or localized)
export function fDate(date, options) {
    const d = new Date(date)
    if (isNaN(d.getTime())) {
        return 'Invalid date'
    }

    // Default format: YYYY-MM-DD
    const defaultOptions = {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }

    const formatter = new Intl.DateTimeFormat(
        'en-GB', // gives DD/MM/YYYY in en-GB
        options ?? defaultOptions
    )
    return formatter.format(d)
}

export const isValidArray = (data) =>
    data && Array.isArray(data) && data.length > 0

export const isValidObject = (data) =>
    typeof data === 'object' && data !== null && Object.keys(data).length !== 0

export const createSelectOptions = (option) => {
    if (option) {
        return {
            label: option || '',
            value: option || '',
        }
    }
    return ''
}
